import { Plugin } from '../services/plugin-service.js';
import { LlmailConfig } from './config.js';
import { createRequire } from 'module';
import { fileURLToPath } from 'url';
import path from 'path';
import { logger } from './logger.js';

interface PluginConfig {
  name: string;
  config?: Record<string, any>;
}

/**
 * Normalize plugin configuration to a consistent format
 */
function normalizePluginConfig(plugins: LlmailConfig['plugins']): PluginConfig[] {
  if (!plugins) return [];

  if (typeof plugins === 'string') {
    // Single plugin string
    return [{ name: plugins }];
  }

  if (Array.isArray(plugins)) {
    return plugins.map(plugin => {
      if (typeof plugin === 'string') {
        return { name: plugin };
      }
      return plugin;
    });
  }

  return [];
}

/**
 * Load a plugin by name
 */
async function loadPlugin(name: string): Promise<Plugin> {
  try {
    logger.debug(`Loading plugin: ${name}`);
    // First try dynamic import which works with both real and mocked modules
    const plugin = await import(name).catch(async (error) => {
      logger.debug(`Dynamic import failed for ${name}, trying require:`, error);
      // If dynamic import fails, try require as fallback for CommonJS modules
      const require = createRequire(import.meta.url);
      try {
        const module = require(name);
        logger.debug(`Required module ${name}:`, module);
        return module;
      } catch (requireError) {
        logger.debug(`Require failed for ${name}:`, requireError);
        throw requireError;
      }
    });
    
    logger.debug(`Plugin module loaded: ${name}`, { exports: Object.keys(plugin) });
    
    // Handle both default exports and named exports
    const PluginClass = plugin.default || plugin;
    logger.debug(`Plugin class found:`, PluginClass);
    
    if (typeof PluginClass !== 'function' && !PluginClass.prototype) {
      logger.debug(`Plugin ${name} exports:`, plugin);
      throw new Error(`Plugin ${name} must export a class or constructor function`);
    }
    
    const instance = new PluginClass();
    logger.debug(`Plugin instance created: ${name}`, {
      commands: instance.registerCommands?.() || [],
      types: instance.registerTypes?.() || [],
      activeStates: instance.registerActiveStates?.() || [],
      inactiveReasons: instance.registerInactiveReasons?.() || []
    });
    
    return instance;
  } catch (error: any) {
    logger.error(`Failed to load plugin ${name}:`, error);
    throw new Error(`Failed to load plugin ${name}: ${error.message}`);
  }
}

/**
 * Load and configure plugins from config
 */
export async function loadPlugins(config: LlmailConfig): Promise<Plugin[]> {
  const pluginConfigs = normalizePluginConfig(config.plugins);
  const plugins: Plugin[] = [];

  logger.debug('Loading plugins from config:', pluginConfigs);
  for (const pluginConfig of pluginConfigs) {
    try {
      const plugin = await loadPlugin(pluginConfig.name);
      
      // Apply configuration if provided
      if (pluginConfig.config && plugin.validateConfig) {
        const result = plugin.validateConfig(pluginConfig.config);
        if (!result.valid) {
          throw new Error(`Invalid config for plugin ${pluginConfig.name}: ${result.errors?.join(', ')}`);
        }
      }

      plugins.push(plugin);
      logger.debug(`Loaded plugin: ${pluginConfig.name}`);
    } catch (error) {
      logger.error(`Failed to load plugin ${pluginConfig.name}:`, error);
      throw error;
    }
  }

  return plugins;
} 